import { BranchTypesService } from '../_services/branch-types/logic/branch-types.service';
import { BranchesService } from '../_services/branches/logic/branches.service';
import { ServicejobsService } from '../_services/servicejobs/logic/servicejobs.service';
import { IMSObject, IMSId } from './ims-object';
import { Material } from './material';
import { OrderItem, OrderItemType } from './order-item';

/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */

export class Stock {
  public static readonly localAppStockId: IMSId = {
    imsId: undefined,
    secondaryId: undefined,
    objRef: 'app/StockPlace',
  };

  constructor(public stockPlaces: StockPlace[]) {}

  public getStockPlaceForId(id: IMSId): StockPlace {
    return this.stockPlaces.find((place) => IMSObject.equals(place.id, id));
  }

  public getAppStockPlace(): StockPlace {
    return this.stockPlaces.find(
      (place) => place.id.objRef === Stock.localAppStockId.objRef,
    );
  }
}

export class StockPlace {
  constructor(
    public id: IMSId,
    public name: string,
    public stockItems: StockItem[],
  ) {}

  public getStockItemForMaterialId(id: IMSId): StockItem {
    if (id === undefined) {
      return undefined;
    }

    return this.stockItems.find((si) => {
      if (
        si.material.id.imsId === id.imsId &&
        si.material.id.secondaryId === id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public getNonVIPStockItems(
    serviceJobsService: ServicejobsService,
    branchService: BranchesService,
    branchTypesService: BranchTypesService,
  ): StockItem[] {
    return this.stockItems
      .map((stockItem) => {
        return new StockItem({
          label: stockItem.label,
          material: stockItem.material,
          orderItems: stockItem.orderItems.filter((orderItem) => {
            let sjob = serviceJobsService.getServiceJobById(
              orderItem?.servicejob?.id,
            );
            let branch = branchService.getBranchById(sjob?.leadingBranch?.id);
            let branchType = branchTypesService.getBranchTypeById(
              branch?.branchType?.id,
            );

            if (branchType === undefined) {
              return true;
            } else {
              return !branchType.isVip;
            }
          }),
        });
      })
      .filter((stockItem) => stockItem.orderItems.length > 0);
  }

  public getVIPStockItems(
    serviceJobsService: ServicejobsService,
    branchService: BranchesService,
    branchTypesService: BranchTypesService,
  ): StockItem[] {
    return this.stockItems
      .map((stockItem) => {
        return new StockItem({
          label: stockItem.label,
          material: stockItem.material,
          orderItems: stockItem.orderItems.filter((orderItem) => {
            let sjob = serviceJobsService.getServiceJobById(
              orderItem?.servicejob?.id,
            );
            let branch = branchService.getBranchById(sjob?.leadingBranch?.id);
            let branchType = branchTypesService.getBranchTypeById(
              branch?.branchType?.id,
            );

            if (branchType === undefined) {
              return false;
            } else {
              return branchType.isVip;
            }
          }),
        });
      })
      .filter((stockItem) => stockItem.orderItems.length > 0);
  }
}

export class StockItem {
  public label: string;
  public material: Material;
  public orderItems: OrderItem[];
  public correction: number;

  constructor(init: Partial<StockItem>) {
    Object.assign(this, init);
  }

  /**
   * Fetches the amt that is in stock but not assigned to a specific servicejob
   * Includes also items that are assigned to a sjob but have not been delivered/accepted
   */
  public getAmtFree(excludeAlternativeArticle: boolean = false): number {
    let freeAmt = 0;
    for (const orderItem of this.orderItems) {
      if (orderItem.type == OrderItemType.RETURN) {
        // Do not count return orderitems as free amtdlv and amtord have other meanings here
        // amtord = amt that should be returned; amtdlv = amt that was actually returned
        continue;
      }
      if (orderItem.servicejob != undefined && orderItem.amtdlv == undefined) {
        // orderitem belongs to a sjob and it was not yet delivered
        // so these items are not free
        continue;
      }

      if (excludeAlternativeArticle && orderItem.isAlternativeArticle == true) {
        continue;
      }

      freeAmt += Math.max((orderItem.amtord || 0) - (orderItem.amtdlv || 0), 0);
    }
    return freeAmt - (this.correction || 0);
  }

  /**
   * Fetches the amt that is initially in stock, does not reduce correction or
   * delivered amt
   */
  public getAmtInitial(
    excludeAlternativeArticle: boolean = false,
    excludeDrivingSales: boolean = false,
  ): number {
    let initialAmt = 0;
    for (const orderItem of this.orderItems) {
      console.log(
        'getAmtInitial',
        orderItem,
        !(
          excludeAlternativeArticle && orderItem.isAlternativeArticle == true
        ) &&
          !(excludeDrivingSales && orderItem.isDrivingSale) &&
          orderItem.type != OrderItemType.RETURN,
      );
      if (
        !(
          excludeAlternativeArticle && orderItem.isAlternativeArticle == true
        ) &&
        !(excludeDrivingSales && orderItem.isDrivingSale) &&
        orderItem.type != OrderItemType.RETURN
      ) {
        initialAmt += orderItem.amtord;
      }
    }
    console.log('getAmtInitial', this.label, 'initialAmt', initialAmt);
    return initialAmt;
  }

  /**
   * Fetches the amt that ist left in the stock. This does not differentiate if the item is reserved
   * for a specific servicejob
   */
  public get amtRemaining(): number {
    let remainingAmt = 0;
    for (const orderItem of this.orderItems) {
      if (orderItem.type === OrderItemType.RETURN) {
        remainingAmt += orderItem.amtdlv || 0;
      } else {
        remainingAmt += Math.max(
          (orderItem.amtord || 0) - (orderItem.amtdlv || 0),
          0,
        );
      }
    }
    return remainingAmt - (this.correction || 0);
  }

  /**
   * Fetches the amt that ist left in the stock. This does not differentiate if the item is reserved
   * for a specific servicejob
   */
  public get amtRemainingWithoutReturnOrderItems(): number {
    let remainingAmt = 0;
    for (const orderItem of this.orderItems) {
      if (orderItem.type !== OrderItemType.RETURN) {
        remainingAmt += Math.max(
          (orderItem.amtord || 0) - (orderItem.amtdlv || 0),
          0,
        );
      }
    }
    return remainingAmt - (this.correction || 0);
  }
}
